import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useMotionValue, animate } from 'framer-motion';
import { ShieldAlert, ShieldCheck, Lock, Fingerprint, AlertTriangle, CheckCircle2, XCircle, ChevronRight } from 'lucide-react';
import { IDENTITY_QUIZ } from '@/config/missionContent';
import CONFIG from '@/config/siteConfig';

/* ═══════════════════════════════════════════════════════════
   HELPER — shuffle array (Fisher-Yates)
   ═══════════════════════════════════════════════════════════ */
function shuffle(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return [];
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/* ═══════════════════════════════════════════════════════════
   HELPER — normalize text for fuzzy comparison
   ═══════════════════════════════════════════════════════════ */
function normalize(str) {
  if (typeof str !== 'string') return '';
  return str.trim().toLowerCase().replace(/[''`]/g, "'").replace(/\s+/g, ' ');
}

function checkTextAnswer(input, question) {
  if (!input || !question?.answer) return false;
  const n = normalize(input);
  if (n === normalize(question.answer)) return true;
  if (Array.isArray(question.acceptAlso)) {
    return question.acceptAlso.some(alt => normalize(alt) === n);
  }
  return false;
}

/* ═══════════════════════════════════════════════════════════
   HELPER — validate question has required fields
   ═══════════════════════════════════════════════════════════ */
function isValidQuestion(q) {
  if (!q || typeof q !== 'object' || !q.id || !q.question || !q.answer) return false;
  const needsOptions = ['multiple_choice', 'runaway', 'morph', 'only_correct'];
  if (needsOptions.includes(q.type) && (!Array.isArray(q.options) || q.options.length < 2)) return false;
  return true;
}

/* ─── Shared spring presets ─── */
const SPRING_SNAPPY = { type: 'spring', stiffness: 500, damping: 25, mass: 0.5 };
const SPRING_BOUNCE = { type: 'spring', stiffness: 400, damping: 15, mass: 0.4 };
const SPRING_ENTER = { type: 'spring', stiffness: 350, damping: 25 };

/* ═══════════════════════════════════════════════════════════
   SCANNING INTRO — biometric-style entry animation
   ═══════════════════════════════════════════════════════════ */
function ScanningIntro({ onDone }) {
  const [scanPhase, setScanPhase] = useState(0);

  useEffect(() => {
    const t1 = setTimeout(() => setScanPhase(1), 1800);
    const t2 = setTimeout(() => setScanPhase(2), 3200);
    const t3 = setTimeout(() => onDone?.(), 4200);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onDone]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[60vh] gap-6"
    >
      <div className="relative">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          className="w-32 h-32 rounded-full"
          style={{ border: '2px solid transparent', borderTopColor: '#f472b6', borderRightColor: 'rgba(244,114,182,0.3)' }}
        />
        <motion.div
          animate={{ rotate: -360 }}
          transition={{ duration: 5, repeat: Infinity, ease: 'linear' }}
          className="absolute inset-2 rounded-full"
          style={{ border: '1.5px solid transparent', borderTopColor: '#4ade80', borderLeftColor: 'rgba(74,222,128,0.2)' }}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div animate={{ scale: [1, 1.15, 1], opacity: [0.6, 1, 0.6] }} transition={{ duration: 2, repeat: Infinity }}>
            <Fingerprint size={40} style={{ color: scanPhase >= 2 ? '#4ade80' : '#f472b6' }} />
          </motion.div>
        </div>
      </div>

      <div className="text-center">
        <AnimatePresence mode="wait">
          {scanPhase === 0 && (
            <motion.p key="scan" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="text-sm font-terminal tracking-[0.2em]" style={{ color: '#f472b6' }}>{IDENTITY_QUIZ.scanning.phase1}</motion.p>
          )}
          {scanPhase === 1 && (
            <motion.p key="analyze" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="text-sm font-terminal tracking-[0.2em]" style={{ color: '#fbbf24' }}>{IDENTITY_QUIZ.scanning.phase2}</motion.p>
          )}
          {scanPhase === 2 && (
            <motion.p key="verify" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
              className="text-sm font-terminal tracking-[0.2em]" style={{ color: '#4ade80' }}>{IDENTITY_QUIZ.scanning.phase3}</motion.p>
          )}
        </AnimatePresence>
      </div>

      <motion.div
        className="absolute left-0 right-0 h-[2px] pointer-events-none"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(244,114,182,0.5), transparent)' }}
        animate={{ top: ['20%', '80%', '20%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   PROGRESS BAR — segmented spy-style
   ═══════════════════════════════════════════════════════════ */
function QuizProgress({ current, total, attempts, maxAttempts }) {
  return (
    <div className="w-full max-w-lg mx-auto mb-8">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldAlert size={14} style={{ color: '#f472b6' }} />
          <span className="text-xs font-terminal tracking-[0.2em]" style={{ color: 'rgba(244,114,182,0.6)' }}>
            QUESTION {Math.min(current + 1, total)} OF {total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {Array.from({ length: maxAttempts }, (_, i) => (
            <motion.div
              key={i}
              className="w-2 h-2 rounded-full"
              animate={i < attempts ? { scale: [1, 0.8], opacity: 0.2 } : { scale: 1, opacity: 1 }}
              style={{
                background: i < attempts ? '#ef4444' : '#4ade80',
                boxShadow: i < attempts ? 'none' : '0 0 6px rgba(74,222,128,0.4)',
              }}
            />
          ))}
          <span className="text-xs font-terminal tracking-wider ml-1" style={{ color: 'rgba(74,222,128,0.4)' }}>
            {Math.max(0, maxAttempts - attempts)} {IDENTITY_QUIZ.quiz.attemptsLeft}
          </span>
        </div>
      </div>

      <div className="flex gap-1.5">
        {Array.from({ length: total }, (_, i) => {
          const isCompleted = i < current;
          const isActive = i === current;
          return (
            <div key={i} className="flex-1 h-[4px] rounded-full overflow-hidden"
              style={{ background: 'rgba(244,114,182,0.1)' }}>
              <motion.div
                className="h-full rounded-full"
                animate={{ width: isCompleted ? '100%' : '0%' }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                style={{
                  background: 'linear-gradient(90deg, #4ade80, #22c55e)',
                  boxShadow: '0 0 6px rgba(74,222,128,0.3)',
                }}
              />
              {isActive && (
                <motion.div
                  className="h-full rounded-full -mt-[4px]"
                  animate={{ width: ['0%', '30%', '0%'], opacity: [0, 0.6, 0] }}
                  transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  style={{ background: 'linear-gradient(90deg, #f472b6, #ec4899)', boxShadow: '0 0 8px rgba(244,114,182,0.4)' }}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MULTIPLE CHOICE QUESTION
   ═══════════════════════════════════════════════════════════ */
function MultipleChoiceQuestion({ question, onAnswer, disabled }) {
  const [selected, setSelected] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const shuffledOptions = useMemo(() => {
    if (!Array.isArray(question?.options)) return [];
    return shuffle(question.options);
  }, [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback((option) => {
    if (disabled || feedback) return;
    setSelected(option);
    const isCorrect = option === question?.answer;
    setFeedback(isCorrect ? 'correct' : 'wrong');
    setTimeout(() => {
      onAnswer?.(isCorrect);
      setSelected(null);
      setFeedback(null);
    }, isCorrect ? 800 : 1000);
  }, [disabled, feedback, question?.answer, onAnswer]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={SPRING_ENTER}
      className="w-full max-w-lg mx-auto"
    >
      <motion.h3 initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
        className="text-lg sm:text-xl font-rajdhani font-semibold text-white mb-6 text-center">
        {question?.question}
      </motion.h3>

      {question?.hint && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="text-xs font-terminal text-center mb-6 tracking-wider" style={{ color: 'rgba(251,191,36,0.4)' }}>
          {IDENTITY_QUIZ.quiz.hintLabel} {question.hint}
        </motion.p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {shuffledOptions.map((option, i) => {
          const isSelected = selected === option;
          const isCorrectOption = feedback === 'correct' && isSelected;
          const isWrongOption = feedback === 'wrong' && isSelected;
          const revealCorrect = feedback === 'wrong' && option === question?.answer;

          return (
            <motion.button
              key={option}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + i * 0.04, ...SPRING_SNAPPY }}
              whileHover={!disabled && !feedback ? { scale: 1.04, y: -2, transition: { duration: 0.1 } } : {}}
              whileTap={!disabled && !feedback ? { scale: 0.96, transition: { duration: 0.05 } } : {}}
              onClick={() => handleSelect(option)}
              disabled={disabled || !!feedback}
              className="relative px-5 py-4 rounded-xl font-rajdhani text-sm font-medium tracking-wide text-left overflow-hidden"
              style={{
                background: isCorrectOption ? 'rgba(74,222,128,0.15)' : isWrongOption ? 'rgba(239,68,68,0.15)' : revealCorrect ? 'rgba(74,222,128,0.1)' : 'rgba(244,114,182,0.06)',
                border: `1px solid ${isCorrectOption ? 'rgba(74,222,128,0.5)' : isWrongOption ? 'rgba(239,68,68,0.5)' : revealCorrect ? 'rgba(74,222,128,0.3)' : 'rgba(244,114,182,0.15)'}`,
                color: isCorrectOption ? '#4ade80' : isWrongOption ? '#ef4444' : revealCorrect ? '#4ade80' : '#e2e8f0',
                cursor: disabled || feedback ? 'default' : 'pointer',
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-terminal shrink-0"
                  style={{
                    background: isCorrectOption ? 'rgba(74,222,128,0.2)' : isWrongOption ? 'rgba(239,68,68,0.2)' : 'rgba(244,114,182,0.1)',
                    border: `1px solid ${isCorrectOption ? 'rgba(74,222,128,0.4)' : isWrongOption ? 'rgba(239,68,68,0.4)' : 'rgba(244,114,182,0.2)'}`,
                  }}>
                  {isCorrectOption ? <CheckCircle2 size={12} /> : isWrongOption ? <XCircle size={12} /> : String.fromCharCode(65 + i)}
                </span>
                {option}
              </span>

              {!feedback && (
                <motion.div className="absolute inset-0 pointer-events-none" initial={{ x: '-100%' }} whileHover={{ x: '200%' }}
                  transition={{ duration: 0.4 }} style={{ background: 'linear-gradient(90deg, transparent, rgba(244,114,182,0.06), transparent)', width: '40%' }} />
              )}
              {isWrongOption && (
                <motion.div className="absolute inset-0 rounded-xl pointer-events-none"
                  animate={{ x: [0, -4, 4, -3, 3, -1, 1, 0] }} transition={{ duration: 0.35 }}
                  style={{ border: '1px solid rgba(239,68,68,0.3)' }} />
              )}
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   RUNAWAY QUESTION — wrong option flees from the cursor!
   ═══════════════════════════════════════════════════════════ */
function RunawayQuestion({ question, onAnswer, disabled }) {
  const [feedback, setFeedback] = useState(null);
  const wrongX = useMotionValue(0);
  const wrongY = useMotionValue(0);
  const containerRef = useRef(null);
  const runawayCount = useRef(0);

  const correctOption = question?.answer;
  const wrongOption = Array.isArray(question?.options) ? question.options.find(o => o !== correctOption) : null;

  const handleCorrect = useCallback(() => {
    if (disabled || feedback) return;
    setFeedback('correct');
    setTimeout(() => { onAnswer?.(true); setFeedback(null); }, 800);
  }, [disabled, feedback, onAnswer]);

  const handleRunaway = useCallback(() => {
    if (!containerRef.current || feedback) return;
    const rect = containerRef.current.getBoundingClientRect();
    const maxX = rect.width / 2 - 80;
    const maxY = rect.height / 2 - 30;
    runawayCount.current += 1;

    const intensity = Math.min(runawayCount.current * 0.3, 1.5);
    const newX = (Math.random() - 0.5) * maxX * 2 * intensity;
    const newY = (Math.random() - 0.5) * maxY * 2 * intensity;
    const springConfig = { type: 'spring', stiffness: 600, damping: 18, mass: 0.4 };
    animate(wrongX, newX, springConfig);
    animate(wrongY, newY, springConfig);
  }, [feedback, wrongX, wrongY]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={SPRING_ENTER}
      className="w-full max-w-lg mx-auto"
    >
      <motion.h3 initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
        className="text-lg sm:text-xl font-rajdhani font-semibold text-white mb-6 text-center">
        {question?.question}
      </motion.h3>

      <AnimatePresence>
        {runawayCount.current >= 3 && !feedback && (
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="text-xs font-terminal text-center mb-4 tracking-wider" style={{ color: 'rgba(251,191,36,0.5)' }}>
            {IDENTITY_QUIZ.quiz.hintLabel} {IDENTITY_QUIZ.quiz.hintRunaway}
          </motion.p>
        )}
      </AnimatePresence>

      <div ref={containerRef} className="relative flex flex-col sm:flex-row gap-4 items-center justify-center min-h-[160px]">
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, ...SPRING_BOUNCE }}
          whileHover={!feedback ? { scale: 1.08, y: -3, transition: { duration: 0.1 } } : {}}
          whileTap={!feedback ? { scale: 0.93, transition: { duration: 0.05 } } : {}}
          onClick={handleCorrect}
          disabled={disabled || !!feedback}
          className="relative px-8 py-4 rounded-xl font-rajdhani text-base font-semibold tracking-wide overflow-hidden min-w-[180px]"
          style={{
            background: feedback === 'correct' ? 'rgba(74,222,128,0.2)' : 'rgba(244,114,182,0.08)',
            border: `1.5px solid ${feedback === 'correct' ? 'rgba(74,222,128,0.6)' : 'rgba(244,114,182,0.2)'}`,
            color: feedback === 'correct' ? '#4ade80' : '#e2e8f0',
            cursor: disabled || feedback ? 'default' : 'pointer',
          }}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {feedback === 'correct' && <CheckCircle2 size={16} />}
            {correctOption}
          </span>
        </motion.button>

        {!feedback && wrongOption && (
          <motion.button
            transition={SPRING_BOUNCE}
            onHoverStart={handleRunaway}
            onClick={handleRunaway}
            className="relative px-8 py-4 rounded-xl font-rajdhani text-base font-semibold tracking-wide overflow-hidden min-w-[180px]"
            whileHover={{ rotate: [0, -5, 5, -3, 0], transition: { duration: 0.2 } }}
            style={{
              x: wrongX,
              y: wrongY,
              background: 'rgba(239,68,68,0.06)',
              border: '1.5px solid rgba(239,68,68,0.15)',
              color: 'rgba(239,68,68,0.6)',
              cursor: 'pointer',
            }}
          >
            <span className="relative z-10">{wrongOption}</span>
          </motion.button>
        )}

        {feedback === 'correct' && wrongOption && (
          <motion.div
            initial={{ opacity: 0.6, scale: 1 }}
            animate={{ opacity: 0, scale: 0.3, rotate: 45, y: 80 }}
            transition={{ duration: 0.5, ease: 'easeIn' }}
            className="px-8 py-4 rounded-xl font-rajdhani text-base font-semibold min-w-[180px] text-center"
            style={{ background: 'rgba(239,68,68,0.06)', border: '1.5px solid rgba(239,68,68,0.15)', color: 'rgba(239,68,68,0.4)' }}
          >
            {wrongOption}
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   MORPH QUESTION — wrong option's text snaps to correct on hover
   ═══════════════════════════════════════════════════════════ */
function MorphQuestion({ question, onAnswer, disabled }) {
  const [feedback, setFeedback] = useState(null);
  const [isWrongHovered, setIsWrongHovered] = useState(false);

  const correctOption = question?.answer;
  const wrongOption = Array.isArray(question?.options) ? question.options.find(o => o !== correctOption) : null;

  const handleCorrect = useCallback(() => {
    if (disabled || feedback) return;
    setFeedback('correct');
    setTimeout(() => { onAnswer?.(true); setFeedback(null); }, 800);
  }, [disabled, feedback, onAnswer]);

  const handleWrongClick = useCallback(() => {
    if (disabled || feedback) return;
    if (isWrongHovered) {
      setFeedback('correct');
      setTimeout(() => { onAnswer?.(true); setFeedback(null); }, 800);
    }
  }, [disabled, feedback, isWrongHovered, onAnswer]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={SPRING_ENTER}
      className="w-full max-w-lg mx-auto"
    >
      <motion.h3 initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
        className="text-lg sm:text-xl font-rajdhani font-semibold text-white mb-6 text-center">
        {question?.question}
      </motion.h3>

      <div className="flex flex-col sm:flex-row gap-4 items-center justify-center">
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, ...SPRING_BOUNCE }}
          whileHover={!feedback ? { scale: 1.08, y: -3, transition: { duration: 0.1 } } : {}}
          whileTap={!feedback ? { scale: 0.93, transition: { duration: 0.05 } } : {}}
          onClick={handleCorrect}
          disabled={disabled || !!feedback}
          className="relative px-8 py-4 rounded-xl font-rajdhani text-base font-semibold tracking-wide overflow-hidden min-w-[180px]"
          style={{
            background: feedback === 'correct' ? 'rgba(74,222,128,0.2)' : 'rgba(244,114,182,0.08)',
            border: `1.5px solid ${feedback === 'correct' ? 'rgba(74,222,128,0.6)' : 'rgba(244,114,182,0.2)'}`,
            color: feedback === 'correct' ? '#4ade80' : '#e2e8f0',
            cursor: disabled || feedback ? 'default' : 'pointer',
          }}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">
            {feedback === 'correct' && <CheckCircle2 size={16} />}
            {correctOption}
          </span>
        </motion.button>

        {!feedback && wrongOption && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15, ...SPRING_BOUNCE }}
            whileHover={{ scale: 1.08, y: -3, transition: { duration: 0.1 } }}
            whileTap={{ scale: 0.93, transition: { duration: 0.05 } }}
            onHoverStart={() => setIsWrongHovered(true)}
            onHoverEnd={() => setIsWrongHovered(false)}
            onClick={handleWrongClick}
            className="relative px-8 py-4 rounded-xl font-rajdhani text-base font-semibold tracking-wide overflow-hidden min-w-[180px]"
            style={{
              background: isWrongHovered ? 'rgba(74,222,128,0.08)' : 'rgba(239,68,68,0.06)',
              border: `1.5px solid ${isWrongHovered ? 'rgba(74,222,128,0.3)' : 'rgba(239,68,68,0.15)'}`,
              color: isWrongHovered ? '#4ade80' : 'rgba(239,68,68,0.6)',
              cursor: 'pointer',
              transition: 'background 0.05s, border-color 0.05s, color 0.05s',
            }}
          >
            <span className="relative z-10 flex items-center justify-center gap-2">
              {isWrongHovered ? correctOption : wrongOption}
            </span>
          </motion.button>
        )}
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ONLY_CORRECT QUESTION — wrong options are dimmed
   ═══════════════════════════════════════════════════════════ */
function OnlyCorrectQuestion({ question, onAnswer, disabled }) {
  const [feedback, setFeedback] = useState(null);
  const [shakeId, setShakeId] = useState(null);
  const shuffledOptions = useMemo(() => {
    if (!Array.isArray(question?.options)) return [];
    return shuffle(question.options);
  }, [question?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = useCallback((option) => {
    if (disabled || feedback) return;
    if (option === question?.answer) {
      setFeedback('correct');
      setTimeout(() => { onAnswer?.(true); setFeedback(null); }, 800);
    } else {
      setShakeId(option);
      setTimeout(() => setShakeId(null), 400);
    }
  }, [disabled, feedback, question?.answer, onAnswer]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={SPRING_ENTER}
      className="w-full max-w-lg mx-auto"
    >
      <motion.h3 initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
        className="text-lg sm:text-xl font-rajdhani font-semibold text-white mb-6 text-center">
        {question?.question}
      </motion.h3>

      {question?.hint && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="text-xs font-terminal text-center mb-6 tracking-wider" style={{ color: 'rgba(251,191,36,0.4)' }}>
          {IDENTITY_QUIZ.quiz.hintLabel} {question.hint}
        </motion.p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {shuffledOptions.map((option, i) => {
          const isCorrect = option === question?.answer;
          const isLocked = !isCorrect;
          const isShaking = shakeId === option;
          const isSelected = feedback === 'correct' && isCorrect;

          return (
            <motion.button
              key={option}
              initial={{ opacity: 0, y: 12 }}
              animate={{
                opacity: isLocked ? 0.35 : 1, y: 0,
                x: isShaking ? [0, -6, 6, -4, 4, -2, 2, 0] : 0,
              }}
              transition={isShaking
                ? { x: { duration: 0.35 } }
                : { delay: 0.05 + i * 0.04, ...SPRING_SNAPPY }
              }
              whileHover={isCorrect && !feedback ? { scale: 1.04, y: -2, transition: { duration: 0.1 } } : {}}
              whileTap={isCorrect && !feedback ? { scale: 0.96, transition: { duration: 0.05 } } : {}}
              onClick={() => handleSelect(option)}
              className="relative px-5 py-4 rounded-xl font-rajdhani text-sm font-medium tracking-wide text-left overflow-hidden"
              style={{
                background: isSelected ? 'rgba(74,222,128,0.15)' : 'rgba(244,114,182,0.06)',
                border: `1px solid ${isSelected ? 'rgba(74,222,128,0.5)' : 'rgba(244,114,182,0.15)'}`,
                color: isSelected ? '#4ade80' : '#e2e8f0',
                cursor: isLocked ? 'not-allowed' : feedback ? 'default' : 'pointer',
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-terminal shrink-0"
                  style={{
                    background: isSelected ? 'rgba(74,222,128,0.2)' : 'rgba(244,114,182,0.1)',
                    border: `1px solid ${isSelected ? 'rgba(74,222,128,0.4)' : 'rgba(244,114,182,0.2)'}`,
                  }}>
                  {isSelected ? <CheckCircle2 size={12} /> : String.fromCharCode(65 + i)}
                </span>
                {option}
              </span>
            </motion.button>
          );
        })}
      </div>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   TEXT INPUT QUESTION
   ═══════════════════════════════════════════════════════════ */
function TextInputQuestion({ question, onAnswer, disabled }) {
  const [value, setValue] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showError, setShowError] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(t);
  }, []);

  const handleSubmit = useCallback((e) => {
    e?.preventDefault();
    if (disabled || feedback) return;
    const trimmed = value.trim();
    if (!trimmed) {
      setShowError(true);
      setTimeout(() => setShowError(false), 2000);
      return;
    }
    const isCorrect = checkTextAnswer(trimmed, question);
    setFeedback(isCorrect ? 'correct' : 'wrong');
    setTimeout(() => {
      onAnswer?.(isCorrect);
      setValue('');
      setFeedback(null);
    }, isCorrect ? 800 : 1000);
  }, [disabled, feedback, value, question, onAnswer]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={SPRING_ENTER}
      className="w-full max-w-lg mx-auto"
    >
      <motion.h3 initial={{ opacity: 0, x: -15 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 }}
        className="text-lg sm:text-xl font-rajdhani font-semibold text-white mb-6 text-center">
        {question?.question}
      </motion.h3>

      <form onSubmit={handleSubmit} className="space-y-4">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="relative">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={disabled || !!feedback}
            placeholder={question?.placeholder || 'Type your answer...'}
            className="w-full px-5 py-4 rounded-xl font-rajdhani text-base text-white placeholder:text-white/20 outline-none"
            style={{
              background: feedback === 'correct' ? 'rgba(74,222,128,0.1)' : feedback === 'wrong' ? 'rgba(239,68,68,0.1)' : 'rgba(244,114,182,0.06)',
              border: `1px solid ${feedback === 'correct' ? 'rgba(74,222,128,0.5)' : feedback === 'wrong' ? 'rgba(239,68,68,0.5)' : showError ? 'rgba(239,68,68,0.4)' : 'rgba(244,114,182,0.2)'}`,
              boxShadow: feedback === 'correct' ? '0 0 20px rgba(74,222,128,0.15)' : feedback === 'wrong' ? '0 0 20px rgba(239,68,68,0.1)' : '0 0 20px rgba(244,114,182,0.05)',
              transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
            }}
          />
          {!value && !feedback && (
            <motion.span className="absolute right-5 top-1/2 -translate-y-1/2 text-sm font-terminal"
              animate={{ opacity: [0, 1, 0] }} transition={{ duration: 1.2, repeat: Infinity }}
              style={{ color: 'rgba(244,114,182,0.3)' }}>_</motion.span>
          )}
          <AnimatePresence>
            {feedback && (
              <motion.div initial={{ opacity: 0, scale: 0 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0 }}
                transition={{ duration: 0.15 }}
                className="absolute right-4 top-1/2 -translate-y-1/2">
                {feedback === 'correct' ? <CheckCircle2 size={20} style={{ color: '#4ade80' }} /> : <XCircle size={20} style={{ color: '#ef4444' }} />}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <AnimatePresence>
          {showError && (
            <motion.p initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
              className="text-xs font-terminal tracking-wider text-center" style={{ color: '#ef4444' }}>
              PLEASE ENTER YOUR ANSWER
            </motion.p>
          )}
        </AnimatePresence>

        <motion.button
          type="submit"
          disabled={disabled || !!feedback}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          whileHover={!disabled && !feedback ? { scale: 1.03, transition: { duration: 0.1 } } : {}}
          whileTap={!disabled && !feedback ? { scale: 0.96, transition: { duration: 0.05 } } : {}}
          className="w-full px-5 py-3.5 rounded-xl font-orbitron text-xs tracking-[0.15em] font-medium relative overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, rgba(244,114,182,0.15), rgba(167,139,250,0.1))',
            border: '1px solid rgba(244,114,182,0.25)',
            color: '#f9a8d4',
            cursor: disabled || feedback ? 'default' : 'pointer',
          }}
        >
          <span className="relative z-10 flex items-center justify-center gap-2">SUBMIT ANSWER <ChevronRight size={14} /></span>
          <motion.div className="absolute inset-0" animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
            style={{ background: 'linear-gradient(90deg, transparent, rgba(244,114,182,0.08), transparent)', width: '40%' }} />
        </motion.button>
      </form>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   FEEDBACK FLASH — screen overlay on correct/wrong
   ═══════════════════════════════════════════════════════════ */
function FeedbackFlash({ type }) {
  if (!type) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: [0, 0.2, 0] }}
      transition={{ duration: 0.5 }}
      className="fixed inset-0 pointer-events-none z-50"
      style={{
        background: type === 'correct'
          ? 'radial-gradient(circle, rgba(74,222,128,0.3), transparent 60%)'
          : 'radial-gradient(circle, rgba(239,68,68,0.3), transparent 60%)',
      }}
    />
  );
}

/* ═══════════════════════════════════════════════════════════
   LOCKOUT SCREEN
   ═══════════════════════════════════════════════════════════ */
function LockoutScreen({ onRetry }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
      <motion.div animate={{ scale: [1, 1.1, 1], rotate: [0, -5, 5, 0] }} transition={{ duration: 2, repeat: Infinity }}>
        <Lock size={56} style={{ color: '#ef4444' }} />
      </motion.div>
      <div>
        <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="text-2xl font-orbitron font-bold tracking-[0.2em] mb-2"
          style={{ color: '#ef4444', textShadow: '0 0 20px rgba(239,68,68,0.3)' }}>{IDENTITY_QUIZ.lockout.heading}</motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
          className="text-sm font-terminal tracking-wider" style={{ color: 'rgba(239,68,68,0.5)' }}>{IDENTITY_QUIZ.lockout.status}</motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="text-xs font-terminal tracking-wider mt-1" style={{ color: 'rgba(251,191,36,0.4)' }}>
          {IDENTITY_QUIZ.lockout.message}
        </motion.p>
      </div>
      <motion.button initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1 }}
        whileHover={{ scale: 1.05, transition: { duration: 0.1 } }} whileTap={{ scale: 0.95, transition: { duration: 0.05 } }} onClick={() => onRetry?.()}
        className="mt-4 px-8 py-3 rounded-xl font-orbitron text-xs tracking-[0.15em] relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(239,68,68,0.05))', border: '1px solid rgba(239,68,68,0.3)', color: '#fca5a5' }}>
        <span className="relative z-10 flex items-center gap-2"><AlertTriangle size={14} /> {IDENTITY_QUIZ.lockout.retryButton}</span>
      </motion.button>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   SUCCESS SCREEN
   ═══════════════════════════════════════════════════════════ */
function SuccessScreen({ onProceed }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="flex flex-col items-center justify-center min-h-[60vh] gap-6 text-center">
      <div className="relative">
        <motion.div initial={{ scale: 0 }} animate={{ scale: [0, 1.5, 1] }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-28 h-28 rounded-full flex items-center justify-center"
          style={{ background: 'rgba(74,222,128,0.08)', border: '2px solid rgba(74,222,128,0.3)', boxShadow: '0 0 40px rgba(74,222,128,0.15)' }}>
          <motion.div initial={{ scale: 0, rotate: -180 }} animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, ...SPRING_BOUNCE }}>
            <ShieldCheck size={42} style={{ color: '#4ade80' }} />
          </motion.div>
        </motion.div>
        <motion.div initial={{ scale: 0.8, opacity: 0.5 }} animate={{ scale: 2.5, opacity: 0 }}
          transition={{ duration: 1.2, delay: 0.2 }} className="absolute inset-0 rounded-full"
          style={{ border: '1px solid rgba(74,222,128,0.3)' }} />
      </div>
      <div>
        <motion.h2 initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
          className="text-2xl font-orbitron font-bold tracking-[0.2em] mb-2"
          style={{ color: '#4ade80', textShadow: '0 0 25px rgba(74,222,128,0.3)' }}>{IDENTITY_QUIZ.success.heading}</motion.h2>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6 }}
          className="text-sm font-rajdhani tracking-wider" style={{ color: 'rgba(74,222,128,0.6)' }}>
          {IDENTITY_QUIZ.success.welcome}
        </motion.p>
      </div>
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
        className="max-w-sm w-full rounded-lg px-4 py-3"
        style={{ background: 'rgba(74,222,128,0.04)', border: '1px solid rgba(74,222,128,0.1)' }}>
        {IDENTITY_QUIZ.success.stats.map((text, i) => ({ text, delay: 1.0 + i * 0.3 })).map((line, i) => (
          <motion.p key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: line.delay }}
            className="text-xs font-terminal tracking-wider py-0.5" style={{ color: 'rgba(74,222,128,0.5)' }}>
            {'>'} {line.text}
          </motion.p>
        ))}
      </motion.div>
      <motion.button initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 2 }}
        whileHover={{ scale: 1.06, transition: { duration: 0.1 } }} whileTap={{ scale: 0.94, transition: { duration: 0.05 } }} onClick={() => onProceed?.()}
        className="mt-4 px-10 py-4 rounded-2xl font-rajdhani text-base font-medium text-white relative overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #ec4899, #f9a8d4)', boxShadow: '0 8px 30px rgba(244,114,182,0.3)' }}>
        <span className="relative z-10">{IDENTITY_QUIZ.success.continueButton}</span>
        <motion.div className="absolute inset-0" animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)', width: '40%' }} />
      </motion.button>
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════════════════
   QUESTION RENDERER — routes to the right component by type
   ═══════════════════════════════════════════════════════════ */
function InvalidQuestionSkip({ onAnswer }) {
  useEffect(() => { onAnswer?.(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function QuestionRenderer({ question, onAnswer, disabled }) {
  if (!question || !isValidQuestion(question)) {
    return <InvalidQuestionSkip onAnswer={onAnswer} />;
  }

  switch (question.type) {
    case 'multiple_choice':
      return <MultipleChoiceQuestion key={question.id} question={question} onAnswer={onAnswer} disabled={disabled} />;
    case 'text_input':
      return <TextInputQuestion key={question.id} question={question} onAnswer={onAnswer} disabled={disabled} />;
    case 'runaway':
      return <RunawayQuestion key={question.id} question={question} onAnswer={onAnswer} disabled={disabled} />;
    case 'morph':
      return <MorphQuestion key={question.id} question={question} onAnswer={onAnswer} disabled={disabled} />;
    case 'only_correct':
      return <OnlyCorrectQuestion key={question.id} question={question} onAnswer={onAnswer} disabled={disabled} />;
    default:
      return <MultipleChoiceQuestion key={question.id} question={question} onAnswer={onAnswer} disabled={disabled} />;
  }
}

/* ═══════════════════════════════════════════════════════════
   MAIN EXPORT — IdentityQuiz
   ═══════════════════════════════════════════════════════════ */
export default function IdentityQuiz({ onComplete }) {
  const quizConfig = IDENTITY_QUIZ || {};
  const rawQuestions = Array.isArray(quizConfig.questions) ? quizConfig.questions : [];
  const validQuestions = rawQuestions.filter(isValidQuestion);
  const maxAttempts = Math.max(1, quizConfig.maxAttempts || 3);

  const [phase, setPhase] = useState(validQuestions.length === 0 ? 'success' : 'scanning');
  const [currentQ, setCurrentQ] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [flashType, setFlashType] = useState(null);
  const [questions, setQuestions] = useState(() => shuffle(validQuestions));

  const handleScanDone = useCallback(() => setPhase('quiz'), []);

  const handleRetry = useCallback(() => {
    setQuestions(shuffle(validQuestions));
    setCurrentQ(0);
    setAttempts(0);
    setFlashType(null);
    setPhase('scanning');
  }, [validQuestions]);

  const handleAnswer = useCallback((isCorrect) => {
    setFlashType(isCorrect ? 'correct' : 'wrong');
    setTimeout(() => setFlashType(null), 500);

    if (isCorrect) {
      const nextQ = currentQ + 1;
      if (nextQ >= questions.length) {
        setTimeout(() => setPhase('success'), 500);
      } else {
        setTimeout(() => setCurrentQ(nextQ), 200);
      }
    } else {
      const newAttempts = attempts + 1;
      setAttempts(newAttempts);
      if (newAttempts >= maxAttempts) {
        setTimeout(() => setPhase('lockout'), 600);
      }
    }
  }, [currentQ, questions.length, attempts, maxAttempts]);

  const currentQuestion = questions[currentQ];

  return (
    <div className="relative min-h-screen px-4 py-8 flex flex-col items-center justify-center">
      {/* Background particles */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
        {Array.from({ length: 20 }, (_, i) => (
          <motion.div key={i} className="absolute w-1 h-1 rounded-full"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, background: ['#f472b6', '#a78bfa', '#fbbf24'][i % 3], opacity: 0.15 }}
            animate={{ y: [0, -30, 0], opacity: [0.1, 0.3, 0.1] }}
            transition={{ duration: 4 + Math.random() * 3, delay: Math.random() * 5, repeat: Infinity }} />
        ))}
      </div>

      {/* HUD header */}
      <div className="w-full max-w-lg mx-auto mb-6 relative z-10">
        <div className="bg-black/50 border border-pink-900/30 rounded px-4 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-pink-400 animate-pulse" />
              <span className="text-xs font-terminal text-pink-700/60 tracking-[3px]">{quizConfig.title || 'IDENTITY VERIFICATION'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Fingerprint size={9} className="text-amber-400/60" />
              <span className="text-xs font-terminal text-amber-400/60 tracking-wider">
                {phase === 'scanning' ? 'SCANNING' : phase === 'quiz' ? 'VERIFYING' : phase === 'success' ? 'CONFIRMED' : 'LOCKED'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Subtitle */}
      {phase === 'quiz' && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="text-xs font-terminal tracking-[0.15em] mb-4 relative z-10" style={{ color: 'rgba(244,114,182,0.4)' }}>
          {quizConfig.subtitle || 'Confirm your identity'}
        </motion.p>
      )}

      {/* Main content */}
      <div className="w-full relative z-10">
        <FeedbackFlash type={flashType} />

        <AnimatePresence mode="wait">
          {phase === 'scanning' && (
            <ScanningIntro key="scanning" onDone={handleScanDone} />
          )}

          {phase === 'quiz' && currentQuestion && (
            <motion.div key="quiz-container" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <QuizProgress current={currentQ} total={questions.length} attempts={attempts} maxAttempts={maxAttempts} />

              <AnimatePresence mode="wait">
                <QuestionRenderer
                  key={currentQuestion.id}
                  question={currentQuestion}
                  onAnswer={handleAnswer}
                  disabled={false}
                />
              </AnimatePresence>
            </motion.div>
          )}

          {phase === 'lockout' && (
            <LockoutScreen key="lockout" onRetry={handleRetry} />
          )}

          {phase === 'success' && (
            <SuccessScreen key="success" onProceed={onComplete} />
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
